import {
  hasConflictingTrailingRegion,
  parseStreetAndCity,
} from "@shared/formats/street-address";
import { isUnknownRecord } from "@shared/validation/narrow";

export interface DeliveryLocation {
  zip: string;
  city: string;
  state: string;
  line1?: string;
  line2?: string;
  latitude?: number;
  longitude?: number;
  savedAddressId?: string;
}

const STORAGE_KEY = "repackify_delivery_location";
const US_ZIP_REGEX = /^\d{5}$/;

export function isValidUsZip(zip: string): boolean {
  return US_ZIP_REGEX.test(zip);
}

export function formatDeliveryLocation(location: DeliveryLocation): string {
  const cityState = [location.city, `${location.state} ${location.zip}`.trim()]
    .filter(Boolean)
    .join(", ");
  const street = [location.line1, location.line2].filter(Boolean).join(", ");
  return street ? `${street}, ${cityState}` : cityState;
}

export function readStoredDeliveryLocation(
  currentOwnerId?: string | null,
): DeliveryLocation | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isUnknownRecord(parsed)) return null;
    const hasOwnerTag = Object.prototype.hasOwnProperty.call(
      parsed,
      "_ownerId",
    );
    if (
      typeof parsed.zip === "string" &&
      isValidUsZip(parsed.zip) &&
      typeof parsed.city === "string" &&
      typeof parsed.state === "string" &&
      hasOwnerTag &&
      (parsed._ownerId ?? null) === (currentOwnerId ?? null)
    ) {
      return {
        zip: parsed.zip,
        city: parsed.city,
        state: parsed.state,
        line1: typeof parsed.line1 === "string" ? parsed.line1 : undefined,
        line2: typeof parsed.line2 === "string" ? parsed.line2 : undefined,
        latitude:
          typeof parsed.latitude === "number" ? parsed.latitude : undefined,
        longitude:
          typeof parsed.longitude === "number" ? parsed.longitude : undefined,
        savedAddressId:
          typeof parsed.savedAddressId === "string"
            ? parsed.savedAddressId
            : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function storeDeliveryLocation(
  location: DeliveryLocation,
  ownerId?: string | null,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...location, _ownerId: ownerId ?? null }),
    );
  } catch {}
}

export function clearStoredDeliveryLocation(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export function claimStoredDeliveryLocation(newOwnerId: string): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (!isUnknownRecord(parsed)) return;
    const hasOwnerTag = Object.prototype.hasOwnProperty.call(
      parsed,
      "_ownerId",
    );
    if (!hasOwnerTag || parsed._ownerId !== null) return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...parsed, _ownerId: newOwnerId }),
    );
  } catch {}
}

export async function lookupPostalCode(
  zip: string,
): Promise<DeliveryLocation | null> {
  if (!isValidUsZip(zip)) return null;
  try {
    const response = await fetch(
      `/api/postal-lookup?zip=${encodeURIComponent(zip)}`,
    );
    if (!response.ok) return null;
    const data: { zipCode: string; city: string; state: string } =
      await response.json();
    return { zip: data.zipCode, city: data.city, state: data.state };
  } catch {
    return null;
  }
}

export function resolveManualAddressLine1(
  display: string,
  location: Pick<DeliveryLocation, "city" | "state" | "zip">,
): string | null | undefined {
  const parsed = resolveManualStreetAndCity(display, location);
  return parsed ? parsed.line1 : parsed;
}

function resolveManualStreetAndCity(
  display: string,
  location: Pick<DeliveryLocation, "city" | "state" | "zip">,
): { line1: string; city: string } | null | undefined {
  const addressPrefix = display
    .trim()
    .replace(/\d{5}(?:-\d{4})?$/, "")
    .replace(/[,\.\s]+$/, "")
    .trim();
  if (!addressPrefix) return undefined;
  const parsed = parseStreetAndCity(
    addressPrefix,
    location.state,
    location.city,
  );
  if (!parsed) return null;
  if (
    hasConflictingTrailingRegion(addressPrefix, location.state, parsed.city)
  ) {
    return null;
  }
  return parsed;
}

export async function resolveManualDeliveryLocation(
  display: string,
  zip: string,
): Promise<DeliveryLocation | null> {
  const location = await lookupPostalCode(zip);
  if (!location) return null;
  const parsed = resolveManualStreetAndCity(display, location);
  if (parsed === null) return null;
  return parsed
    ? { ...location, line1: parsed.line1, city: parsed.city }
    : location;
}
